import { call, put, takeEvery, select } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../reducers';
import { shiftsApi, Shift, ShiftSearchParams } from '../../services/shiftsApi';
import {
  setLoading,
  setError,
  setItems,
  setSelectedItem,
  addItem,
  updateItem,
  removeItem,
  setValidationErrors,
  setPagination,
  setBulkOperationLoading,
  setSuccessMessage,
  FETCH_SHIFTS,
  FETCH_SHIFT_BY_ID,
  CREATE_SHIFT,
  UPDATE_SHIFT,
  DELETE_SHIFT,
  SEARCH_SHIFTS,
  BULK_ACTIVATE,
  BULK_DEACTIVATE,
  BULK_DELETE,
  EXPORT_TO_CSV,
} from '../reducers/shiftsSlice';

// Fetch all Shifts
function* fetchShiftsSaga(action: PayloadAction<ShiftSearchParams | undefined>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    const state: RootState = yield select();
    const { pagination, searchParams } = state.shifts;
    
    const params = {
      page: pagination.page,
      pageSize: pagination.pageSize,
      search: searchParams.search || '',
      active: searchParams.active,
      sortBy: searchParams.sortBy || 'Name',
      sortOrder: searchParams.sortOrder || 'asc',
      ...action.payload,
    };
    
    const response = yield call(shiftsApi.getAll, params);
    
    yield put(setItems(response.data));
    yield put(setPagination({
      total: response.total,
      page: params.page || 1,
      pageSize: params.pageSize || 10,
    }));
  } catch (error: any) {
    console.error('Error fetching Shifts:', error);
    yield put(setError(error.message || 'Failed to fetch Shifts'));
  } finally {
    yield put(setLoading(false));
  }
}

// Fetch Shift by ID
function* fetchShiftByIdSaga(action: PayloadAction<number>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    const shift = yield call(shiftsApi.getById, action.payload);
    yield put(setSelectedItem(shift));
  } catch (error: any) {
    console.error('Error fetching Shift by ID:', error);
    yield put(setError(error.message || 'Failed to fetch Shift'));
  } finally {
    yield put(setLoading(false));
  }
}

// Create Shift
function* createShiftSaga(action: PayloadAction<Omit<Shift, 'Id'>>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    // Validate the request
    const validationErrors = yield call(shiftsApi.validation.validateShift, action.payload);
    if (validationErrors.length > 0) {
      yield put(setValidationErrors(validationErrors));
      return;
    }
    
    const newShift = yield call(shiftsApi.create, action.payload);
    yield put(addItem(newShift));
    yield put(setSuccessMessage('Shift created successfully'));
    
    // Clear success message after 3 seconds
    setTimeout(() => {
      // Note: In a real saga, you'd use a different approach for this
      // This is simplified for demonstration
    }, 3000);
  } catch (error: any) {
    console.error('Error creating Shift:', error);
    yield put(setError(error.message || 'Failed to create Shift'));
  } finally {
    yield put(setLoading(false));
  }
}

// Update Shift
function* updateShiftSaga(action: PayloadAction<{ id: number; shift: Partial<Shift> }>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    const { id, shift } = action.payload;
    
    // Validate the request
    const validationErrors = yield call(shiftsApi.validation.validateShift, shift);
    if (validationErrors.length > 0) {
      yield put(setValidationErrors(validationErrors));
      return;
    }
    
    const updatedShift = yield call(shiftsApi.update, id, shift);
    yield put(updateItem(updatedShift));
    yield put(setSuccessMessage('Shift updated successfully'));
  } catch (error: any) {
    console.error('Error updating Shift:', error);
    yield put(setError(error.message || 'Failed to update Shift'));
  } finally {
    yield put(setLoading(false));
  }
}

// Delete Shift
function* deleteShiftSaga(action: PayloadAction<number>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    const response = yield call(shiftsApi.delete, action.payload);
    if (response.success) {
      yield put(removeItem(action.payload));
      yield put(setSuccessMessage('Shift deleted successfully'));
    } else {
      yield put(setError(response.message || 'Failed to delete Shift'));
    }
  } catch (error: any) {
    console.error('Error deleting Shift:', error);
    yield put(setError(error.message || 'Failed to delete Shift'));
  } finally {
    yield put(setLoading(false));
  }
}

// Search Shifts
function* searchShiftsSaga(action: PayloadAction<ShiftSearchParams>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    const response = yield call(shiftsApi.search, action.payload);
    
    yield put(setItems(response.data));
    yield put(setPagination({
      total: response.total,
      page: action.payload.page || 1,
      pageSize: action.payload.pageSize || 10,
    }));
  } catch (error: any) {
    console.error('Error searching Shifts:', error);
    yield put(setError(error.message || 'Failed to search Shifts'));
  } finally {
    yield put(setLoading(false));
  }
}


// Bulk activate
function* bulkActivateSaga(action: PayloadAction<number[]>): Generator<any, void, any> {
  try {
    yield put(setBulkOperationLoading(true));
    yield put(setError(null));
    
    // Validate the request
    const validationErrors = yield call(shiftsApi.validation.validateBulkOperation, action.payload);
    if (validationErrors.length > 0) {
      yield put(setError(validationErrors[0]));
      return;
    }
    
    const response = yield call(shiftsApi.bulkActivate, action.payload);
    if (response.success) {
      yield put(setSuccessMessage(response.message || 'Shifts activated successfully'));
      // Refresh the list
      yield put({ type: FETCH_SHIFTS });
    } else {
      yield put(setError(response.message || 'Failed to activate Shifts'));
    }
  } catch (error: any) {
    console.error('Error bulk activating Shifts:', error);
    yield put(setError(error.message || 'Failed to activate Shifts'));
  } finally {
    yield put(setBulkOperationLoading(false));
  }
}

// Bulk deactivate
function* bulkDeactivateSaga(action: PayloadAction<number[]>): Generator<any, void, any> {
  try {
    yield put(setBulkOperationLoading(true));
    yield put(setError(null));
    
    // Validate the request
    const validationErrors = yield call(shiftsApi.validation.validateBulkOperation, action.payload);
    if (validationErrors.length > 0) {
      yield put(setError(validationErrors[0]));
      return;
    }
    
    const response = yield call(shiftsApi.bulkDeactivate, action.payload);
    if (response.success) {
      yield put(setSuccessMessage(response.message || 'Shifts deactivated successfully'));
      // Refresh the list
      yield put({ type: FETCH_SHIFTS });
    } else {
      yield put(setError(response.message || 'Failed to deactivate Shifts'));
    }
  } catch (error: any) {
    console.error('Error bulk deactivating Shifts:', error);
    yield put(setError(error.message || 'Failed to deactivate Shifts'));
  } finally {
    yield put(setBulkOperationLoading(false));
  }
}

// Bulk delete
function* bulkDeleteSaga(action: PayloadAction<number[]>): Generator<any, void, any> {
  try {
    yield put(setBulkOperationLoading(true));
    yield put(setError(null));
    
    // Validate the request
    const validationErrors = yield call(shiftsApi.validation.validateBulkOperation, action.payload);
    if (validationErrors.length > 0) {
      yield put(setError(validationErrors[0]));
      return;
    }
    
    const response = yield call(shiftsApi.bulkDelete, action.payload);
    if (response.success) {
      yield put(setSuccessMessage(response.message || 'Shifts deleted successfully'));
      // Refresh the list
      yield put({ type: FETCH_SHIFTS });
    } else {
      yield put(setError(response.message || 'Failed to delete Shifts'));
    }
  } catch (error: any) {
    console.error('Error bulk deleting Shifts:', error);
    yield put(setError(error.message || 'Failed to delete Shifts'));
  } finally {
    yield put(setBulkOperationLoading(false));
  }
}

// Export to CSV
function* exportToCSVSaga(action: PayloadAction<ShiftSearchParams | undefined>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    const state: RootState = yield select();
    const { searchParams } = state.shifts;
    
    const params = {
      ...searchParams,
      ...action.payload,
    };
    
    const blob = yield call(shiftsApi.exportToCSV, params);
    
    // Create download link
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `shifts_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    yield put(setSuccessMessage('Shifts exported successfully'));
  } catch (error: any) {
    console.error('Error exporting Shifts:', error);
    yield put(setError(error.message || 'Failed to export Shifts'));
  } finally {
    yield put(setLoading(false));
  }
}

// Root Shifts Saga
export function* shiftsSaga(): Generator<any, void, any> {
  yield takeEvery(FETCH_SHIFTS, fetchShiftsSaga);
  yield takeEvery(FETCH_SHIFT_BY_ID, fetchShiftByIdSaga);
  yield takeEvery(CREATE_SHIFT, createShiftSaga);
  yield takeEvery(UPDATE_SHIFT, updateShiftSaga);
  yield takeEvery(DELETE_SHIFT, deleteShiftSaga);
  yield takeEvery(SEARCH_SHIFTS, searchShiftsSaga);
  yield takeEvery(BULK_ACTIVATE, bulkActivateSaga);
  yield takeEvery(BULK_DEACTIVATE, bulkDeactivateSaga);
  yield takeEvery(BULK_DELETE, bulkDeleteSaga);
  yield takeEvery(EXPORT_TO_CSV, exportToCSVSaga);
} 